// steps/tabs/meter-readings/MeterReplacementTab.tsx
import { useMeterReplacementCases, useReceiptBillTemplates } from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PAGE_SIZE } from "@shared/utils/constants";
import { AlertCircle, FileText, HelpCircle, Split } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MeterReplacementHowItWorksDialog } from "./MeterReplacementHowItWorksDialog";

interface MeterReplacementTabProps {
  data?: any;
  onTemplateChange?: (template: string) => void;
  onSelectedRowsChange?: (separateRows: string[], combineRows: string[]) => void;
}

const MeterReplacementTab: React.FC<MeterReplacementTabProps> = ({
  data,
  onTemplateChange,
  onSelectedRowsChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [separateSelected, setSeparateSelected] = useState<string[]>([]);
  const [combineSelected, setCombineSelected] = useState<string[]>([]);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const pageSize = PAGE_SIZE;
  const { id, cycleId, action } = useParams();

  const { data: replacementData } = useMeterReplacementCases(
    cycleId,
    currentPage,
    pageSize,
    searchTerm
  );

  const { data: templatesData } = useReceiptBillTemplates();

  // Bill template options
  const billTemplates = Array.isArray(templatesData) 
    ? templatesData.map((t: any) => ({
        value: String(t.templateId || t.id || t.value),
        label: t.title || t.name || t.label,
      }))
    : [];

  // Set default selected template to the first item
  useEffect(() => {
    if (billTemplates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(billTemplates[0].value);
    }
  }, [billTemplates, selectedTemplate]);

  // Notify parent when template changes
  useEffect(() => {
    if (onTemplateChange) {
      onTemplateChange(selectedTemplate);
    }
  }, [selectedTemplate, onTemplateChange]);

  // Notify parent when selected rows change
  useEffect(() => {
    if (onSelectedRowsChange) {
      onSelectedRowsChange(separateSelected, combineSelected);
    }
  }, [separateSelected, combineSelected, onSelectedRowsChange]);

  const handleRowSeparate = (row: any) => {
    const rowId = String(row.id);
    setSeparateSelected((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
    // Remove from combine if exists
    setCombineSelected((prev) => prev.filter((id) => id !== rowId));
  };

  const handleRowCombine = (row: any) => {
    const rowId = String(row.id);
    setCombineSelected((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
    // Remove from separate if exists
    setSeparateSelected((prev) => prev.filter((id) => id !== rowId));
  };

  // Define columns
  const columns: TableColumn<any>[] = [
    {
      key: "accountNo",
      header: "Account No",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
    },
    {
      key: "utilityService",
      header: "Utility Service",
      render: (services) => {
        const servicesArray = Array.isArray(services) ? services : [services].filter(Boolean);
        return (
          <div className="flex gap-2 flex-wrap">
            {servicesArray.length > 0 ? (
              servicesArray.map((service, idx) => (
                <Badge key={idx} variant="secondary">
                  {service}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-gray-700">NA</span>
            )}
          </div>
        );
      },
    },
    {
      key: "oldDeviceNo",
      header: "Old Device No",
    },
    {
      key: "oldMeterNo",
      header: "Old Meter No",
    },
    {
      key: "newDeviceNo",
      header: "New Device No",
    },
    {
      key: "newMeterNo",
      header: "New Meter No",
    },
    {
      key: "daysSinceReplacement",
      header: "Days Since Replacement",
      type: "badge",
      sortable: false,
      width: "w-40",
      badgeConfig: {
        getVariant: () => "secondary",
        getClassName: (value: string) => {
          const days = parseInt(value);
          if (days <= 5) return "bg-green-100 text-green-700";
          if (days <= 10) return "bg-yellow-100 text-yellow-700";
          return "bg-orange-100 text-orange-700";
        },
      },
    },
    {
      key: "billFormat",
      header: "Bill Format",
      type: "custom",
      sortable: false,
      width: "w-48",
      render: (value: string, row: any) => {
        const rowId = String(row.id);
        const isSeparate = separateSelected.includes(rowId);
        const isCombine = combineSelected.includes(rowId);

        return (
          <div className="flex items-center gap-2">
            <Button
              variant={isSeparate ? "default" : "outline"}
              size="sm"
              onClick={() => handleRowSeparate(row)}
              className="flex items-center gap-1 text-xs"
            >
              <FileText className="w-3 h-3" />
              Separate
            </Button>
            <Button
              variant={isCombine ? "default" : "outline"}
              size="sm"
              onClick={() => handleRowCombine(row)}
              className="flex items-center gap-1 text-xs"
            >
              <Split className="w-3 h-3" />
              Combine
            </Button>
          </div>
        );
      },
    },
  ];

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleClearSelection = () => {
    setSeparateSelected([]);
    setCombineSelected([]);
    setSelectedTemplate("");
  };

  // Calculate pagination
  const totalItems = replacementData?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="space-y-4">
      {/* Header with Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-lg">Replacement Cases</h3>
          <Badge className="bg-orange-100 text-orange-700 border-orange-300">
            {replacementData?.count} Cases Found
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowHowItWorksModal(true)}
          className="flex items-center gap-2"
        >
          <HelpCircle className="w-4 h-4" />
          How it works
        </Button>
      </div>

      {/* Action Required Banner */}
      <div className="bg-yellow-50 border-l-4 border-l-yellow-500 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-yellow-900 mb-1">
              Action Required
            </h4>
            <p className="text-sm text-yellow-800">
              Select billing method for each meter replacement case in the table
              below. Your choice determines how consumption is calculated and
              displayed on bills.
            </p>
          </div>
        </div>
      </div>

      {/* Selection and Actions Bar */}
      {(separateSelected.length > 0 || combineSelected.length > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-900">
                {separateSelected.length} separate, {combineSelected.length} combine selected
              </span>
              <Button
                variant="link"
                size="sm"
                onClick={handleClearSelection}
                className="text-blue-700 hover:text-blue-900 p-0"
              >
                Clear Selection
              </Button>
            </div>
            {combineSelected.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">
                  Select bill template for combined bill:
                </span>
                <Select
                  value={selectedTemplate}
                  onValueChange={(e) => setSelectedTemplate(e)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Templates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Templates</SelectItem>
                    {billTemplates.map((template) => (
                      <SelectItem key={template.value} value={template.value}>
                        {template.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg">
        <GenericTable
          data={replacementData?.results || []}
          columns={columns}
          rowKey="id"
          selectable={true}
          searchPlaceholder="Search by account, consumer name, or meter number"
          searchParamName="search"
          searchDebounceMs={300}
          onSearch={handleSearch}
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: pageSize,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
          emptyMessage="No replacement cases found"
        />
      </div>
      <MeterReplacementHowItWorksDialog
        open={showHowItWorksModal}
        onOpenChange={setShowHowItWorksModal}
      />
    </div>
  );
};

export default MeterReplacementTab;
